import React, {useContext, useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { ValidateLogPageNumber } from '../../services/DelayListService/DelayListService';
import { AuthContext } from "../../providers/AuthProvider";
import { ReactSVG } from 'react-svg';
import classes from "./LogPageNumberInput.module.scss";
import { DefectReport } from '../../Model/DefectReport';

interface LogPageNumberInputProps {
    value: string;
    onStartValidation: () => void;
    onCompleteValidation: (isValid: boolean,logPageNumber:string, logPageDetails?: DefectReport) => void;
    onFocusChanged?: (isFocused: boolean) => void;
    onBlur?: () => void;
    itemNumber?: string;
    isNon737Tail?: boolean;
    disabled?: boolean;
    manufacturer: string;
    isLogPageValid: boolean;   
}

export const LogPageNumberInput = forwardRef(({ value, onStartValidation, onCompleteValidation, onFocusChanged, onBlur, itemNumber, isNon737Tail, disabled = false, manufacturer,isLogPageValid}
    : LogPageNumberInputProps, ref) => {
        //Context
        const _authContext = useContext(AuthContext);
        
        //Constants        
        const placeholder = "Log Number";

        //States
        const [isValid, setIsValid] = useState<boolean>(true);
        const [isFocused, setIsFocused] = useState<boolean>(false);
        const [isTouch, setIsTouch] = useState<boolean>(false);
        const [isValidationInProgress, setIsValidationInProgress] = useState<boolean>(false);
        const [logpageErrorMessage, setLogPageErrorMessag] = useState("");
        const [logPagenumber, setLogPageNumber] = useState(value);
        // Ref always tracks the latest intended value so validateLogNumber is not
        // affected by stale closure when called via the imperative refresh() handle
        // before the state has re-rendered (e.g. from TraxLogInfo's auto-refresh effect).
        const logPagenumberRef = React.useRef(value);
        //const [required, setRequired] = useState(isRequired);
        const [manufacturerCode, setManufacturerCode] = useState(manufacturer);

        //Effects
        React.useEffect(() => {
            logPagenumberRef.current = value;
            setLogPageNumber(value);  
                      
        }, [value]);

        useEffect(() => {
            setIsValid(isLogPageValid);
           
             if(!isLogPageValid) {
                setLogPageErrorMessag(getInvalidMessage("Invalid logpage number"));
            }
        }, [isLogPageValid, isNon737Tail, itemNumber]);

        useEffect(() => {
            setManufacturerCode(manufacturer);
        }, [manufacturer]);
        
        //isRequired changed
        // React.useEffect(() => {
        //     setRequired(isRequired);
        //     if(!isValid && logPagenumber === "" && !isRequired) {
        //         setIsValid(true);
        //         onCompleteValidation(true,logPagenumber);                
        //     }
        //     else if(isValid && isTouch && logPagenumber === "" && isRequired) {
        //         setIsValid(false);
        //         setLogPageErrorMessag("Please fill out this field.");
        //         onCompleteValidation(false,logPagenumber,undefined);               
        //     }            
        // }, [isRequired]);

        const hasItemNumber = () => !!itemNumber && itemNumber.trim() !== "";

        const shouldSkipValidationForNon737 = () => isNon737Tail === true && !hasItemNumber();

        const getInvalidMessage = (fallbackMessage: string) => {
            if (isNon737Tail === true && hasItemNumber()) {
                return "Invalid log page and/or item number";
            }
            return fallbackMessage;
        };

        const completeValid = (currentValue: string, logPageDetails?: DefectReport) => {
            setIsValid(true);
            onCompleteValidation(true, currentValue, logPageDetails);
        };

        const completeInvalid = (currentValue: string, message: string) => {
            setIsValid(false);
            setLogPageErrorMessag(message);
            onCompleteValidation(false, currentValue, undefined);
        };

        // Event Handlers
        const validateLogNumber = async (callParentBlur: boolean = true) => {
            // Snapshot the value from the ref so we always use the latest intended value,
            // even if this function is called (e.g. via refresh()) before the state
            // has re-rendered after a prop change.
            const currentValue = logPagenumberRef.current;

            const invokeParentBlur = () => {
                if (callParentBlur && onBlur) {
                    onBlur();
                }
            };

            setIsFocused(false);
            if (callParentBlur) {
                onFocusChanged?.(false);
            }

            if (shouldSkipValidationForNon737()) {
                completeValid(currentValue);
                invokeParentBlur();
                return;
            }

            onStartValidation();

            if (currentValue === "") {
                completeValid(currentValue);
                invokeParentBlur();
                return;
            }

            setIsValidationInProgress(true);
            try {
                const response = await ValidateLogPageNumber(_authContext.getAccessToken(), currentValue, manufacturerCode, itemNumber);
                if (!response.LogTailNbr) {
                    completeInvalid(currentValue, getInvalidMessage("Invalid logpage number"));
                    return;
                }
                completeValid(currentValue, response);
            }
            catch(error: any) {
                console.log(error);
                completeInvalid(currentValue, getInvalidMessage(error.message));
            }
            finally {
                setIsValidationInProgress(false);
                invokeParentBlur();
            }
        }
    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const paste = (event.clipboardData || window.Clipboard).getData('text');
        const trimmedPaste = paste.trim();
        logPagenumberRef.current = trimmedPaste;
        setLogPageNumber(trimmedPaste);
        onCompleteValidation(true, trimmedPaste);
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ' ') {
            event.preventDefault();
        }
    };

        // const handleClearInput = () => {
        //     setLogPageNumber("");
        //     setIsTouch(true);
        //     if(required) {
        //         setIsValid(false);
        //         setLogPageErrorMessag("Please fill out this field.");
        //         onCompleteValidation(false,"",undefined);
        //     }
        //     else {
        //         setIsValid(true);
        //         onCompleteValidation(true, "");
        //     }

        // }

        // Imperative Handlers
        useImperativeHandle(ref, () => ({
            refresh: () => validateLogNumber(false)
        }));


        //Render
        return (
                <div className={classes.DCLogPageInputWrapper}>
                
                <div className={`${classes.DCLogPageInput} ${isValid ? classes.DCLogPageInputValid : classes.DCLogPageInputNotValid} ${disabled ? classes.DCLogPageInputDisabled : ""}`}>
                    {isValid ? null : (
                      <div className={classes.DCLogPageInputErrorIcon}>
                        <ReactSVG src="https://cdn.jsdelivr.net/npm/@alaskaairux/icons@latest/dist/icons/alert/error.svg" />
                      </div>
                    )}
                    <input
                      data-testid="dcTextAreaInput"
                      aria-label="logpagenumber"
                      value={logPagenumber}
                      disabled={ disabled || isValidationInProgress}
                      onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                            logPagenumberRef.current = e.target.value;
                            setLogPageNumber(e.target.value);  
                            {onCompleteValidation(true, e.target.value);}                     
                      }}
                      onBlur={() => validateLogNumber(true)}  
                      onFocus={() => {
                        setIsFocused(true);
                        setIsTouch(true);
                        onFocusChanged?.(true);
                    }}    
                    onPaste={handlePaste}  
                    onKeyDown={handleKeyDown}                 
                    ></input>
                    
                    <span slot="label">{placeholder}</span>
                    </div>  
                    {   isValid ? null : 
                    <div data-testid="dcLogPageInputError" className={classes.DCLogPageInputErrorMessage}>
                        <span>{logpageErrorMessage}</span></div>   }
             </div>
                
        );
});
